/* AI detail (design layout 2a "Ledger"): a KPI strip with the fan path under it,
   one card of four groups (Volume · Tips · PPV · Requests & offers) and the
   interrupts. Money is the AI's Seller share (CONTEXT.md "AI revenue"); everything
   else is what the AI's own counters saw. Customs and calls are counts only — the
   money a fan paid for them is already inside its tips/PPVs. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatStatsResponse, StatisticsResponse } from '../../../api/client';
import { C } from '../../ChatAssistant/compact/tokens';
import { font as f } from '../../ChatAssistant/compact/style';
import { Icon } from '../../ChatAssistant/compact/kit';
import { formatInterruptText, interruptNameToKey } from '../../../utils/formatInterruptText';
import { aiActivity, aiMoney } from './derive';
import { fmt, pct, pick, ratio, share, usd, usd2, type MoneyMode } from './model';

interface Props {
  stats: StatisticsResponse | null;
  chat: ChatStatsResponse | null;
  mode: MoneyMode;
  /** Account surface only: jump to the settings that close a zero. */
  onOpenSubTab?: (id: string) => void;
}

/** An icon for each interrupt, by what it is about; unknown ones get the generic alert. */
const INTERRUPT_ICON: [RegExp, string][] = [
  [/filter/, 'filter'], [/spam/, 'shield'], [/banned|platform_topic/, 'slash'], [/off_platform|external|pay_ext/, 'external-link'],
  [/other_model/, 'users'], [/custom/, 'gift'], [/call|voice|video/, 'phone'], [/pric/, 'tag'], [/body/, 'user'],
  [/looks|look_now/, 'eye'], [/awaiting|promised|waiting/, 'image'], [/sent.*media|media_file|fan_media/, 'paperclip'],
  [/content/, 'grid'], [/money|broke/, 'credit-card'], [/angry|not_happy|unhappy/, 'frown'], [/busy/, 'clock'],
  [/not_agree|refus|decline/, 'x-circle'], [/disengag|stopped/, 'log-out'], [/tipped|tip/, 'gift'], [/purchas/, 'shopping-bag'],
];
const interruptIcon = (name: string) => INTERRUPT_ICON.find(([re]) => re.test(name))?.[1] ?? 'alert-circle';

const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

interface GroupRow { label: string; value: string; aside?: string; strong?: boolean; dim?: boolean }
interface Group { icon: string; title: string; total: string; rows: GroupRow[]; link?: { label: string; to: string }; note?: string }

export function AiDetail({ stats, chat, mode, onOpenSubTab }: Props) {
  const { t } = useTranslation('statsUnified');
  const { t: tChat } = useTranslation('chatAssistant');
  const [allInts, setAllInts] = useState(false);
  const a = aiActivity(chat?.totals);
  const money = aiMoney(stats, mode);
  const chatSales = (['ai', 'team', 'unknown'] as const).reduce((s, k) => s + pick(stats?.money.sellers[k], mode), 0);
  const interrupts = (chat?.interrupts ?? []).map((x) => ({
    name: x.name,
    label: tChat(`interrupts.${interruptNameToKey(x.name)}` as never, { defaultValue: sentence(formatInterruptText(x.name)) }) as string,
    count: x.count,
    icon: interruptIcon(x.name),
  }));
  const intTotal = interrupts.reduce((s, x) => s + x.count, 0);
  const intMax = interrupts[0]?.count || 1;
  const shownInts = allInts ? interrupts : interrupts.slice(0, 6);

  const kpis = [
    { label: t('ai.kpi.revenue'), value: usd(money.revenue), sub: t('ai.kpi.revenueShare', { value: share(money.revenue, chatSales) }), tip: `${t('types.ppvs')} ${usd(money.ppv)} · ${t('types.tips')} ${usd(money.tips)}` },
    { label: t('ai.kpi.chats'), value: fmt(a.chats), sub: t('ai.kpi.newChats', { count: a.newChats, share: share(a.newChats, a.chats) }) },
    { label: t('ai.kpi.reached'), value: pct(ratio(a.payments, a.chats) * 100), sub: t('ai.kpi.reachedOf', { paid: fmt(a.payments), chats: fmt(a.chats) }) },
    { label: t('ai.kpi.perChat'), value: usd2(ratio(money.revenue, a.chats)), sub: t('ai.kpi.asksPerPayment', { value: ratio(a.moneyAsks, a.payments).toFixed(1) }) },
    { label: t('ai.kpi.interrupts'), value: fmt(intTotal), sub: t('ai.kpi.ofChats', { value: share(intTotal, a.chats) }) },
  ];

  // Payments count tips, PPVs, customs and calls, so the step before them counts
  // every ask for money, not only tip asks — otherwise the rate passes 100%.
  const path = [
    { label: t('ai.path.chats'), value: fmt(a.chats) },
    { label: t('ai.path.asks'), value: fmt(a.moneyAsks), conv: pct(ratio(a.moneyAsks, a.chats) * 100), convTip: t('ai.path.asksTip') },
    { label: t('ai.path.payments'), value: fmt(a.payments), conv: pct(ratio(a.payments, a.moneyAsks) * 100), convTip: t('ai.path.paymentsTip') },
  ];

  const ppvZero = money.ppvSold === 0;
  const reqPaid = a.customsPaid + a.vcPaid;
  const groups: Group[] = [
    {
      icon: 'send', title: t('ai.volume.title'), total: fmt(a.messages),
      rows: [
        { label: t('ai.volume.responses'), value: fmt(a.responses) },
        { label: t('ai.volume.sent'), value: fmt(a.messages), aside: t('ai.volume.perResponse', { value: ratio(a.messages, a.responses).toFixed(1) }) },
        { label: t('ai.volume.newChats'), value: fmt(a.newChats), aside: `${share(a.newChats, a.chats)}%` },
        { label: t('ai.volume.perChat'), value: fmt(ratio(a.messages, a.chats)) },
      ],
    },
    {
      icon: 'gift', title: t('ai.tips.title'), total: usd(money.tips),
      rows: [
        { label: t('ai.tips.hot'), value: fmt(a.asksHot), aside: `${share(a.asksHot, a.asks)}%`, dim: !a.asksHot },
        { label: t('ai.tips.normal'), value: fmt(a.asksNormal), aside: `${share(a.asksNormal, a.asks)}%`, dim: !a.asksNormal },
        { label: t('ai.tips.paidSexting'), value: fmt(a.asksPaidSexting), aside: `${share(a.asksPaidSexting, a.asks)}%`, dim: !a.asksPaidSexting },
        // No conversion here: a tip isn't tied to an ask (fans also tip unasked), so tips ÷ asks can pass 100%.
        { label: t('ai.tips.received'), value: fmt(money.tipsCount), strong: true },
        { label: t('ai.tips.average'), value: money.tipsCount ? usd2(money.tips / money.tipsCount) : '—', dim: !money.tipsCount },
      ],
    },
    {
      icon: 'lock', title: t('ai.ppv.title'), total: usd(money.ppv),
      link: { label: t('ai.ppv.link'), to: 'sales-ladder' },
      note: ppvZero ? (a.ppvSent ? t('ai.ppv.zeroSent', { count: a.ppvSent }) : t('ai.ppv.zeroNone')) : undefined,
      rows: [
        { label: t('ai.ppv.sentLadder'), value: fmt(a.ppvSentLadder) },
        { label: t('ai.ppv.sentRequests'), value: fmt(a.ppvSentRequests) },
        { label: t('ai.ppv.sold'), value: fmt(money.ppvSold), aside: a.ppvSent ? pct(ratio(money.ppvSold, a.ppvSent) * 100) : '', strong: true },
        { label: t('ai.ppv.average'), value: money.ppvSold ? usd2(money.ppv / money.ppvSold) : '—', dim: ppvZero },
      ],
    },
    {
      icon: 'inbox', title: t('ai.requests.title'), total: t('ai.requests.paidTotal', { count: reqPaid }),
      link: { label: t('ai.requests.link'), to: 'full-automation' },
      note: reqPaid === 0 && a.customsHandled + a.vcHandled > 0 ? t('ai.requests.zero', { count: a.customsHandled + a.vcHandled }) : undefined,
      rows: [
        { label: t('ai.requests.customs'), value: t('ai.requests.paidOf', { paid: a.customsPaid, handled: a.customsHandled }), dim: !a.customsPaid },
        { label: t('ai.requests.calls'), value: t('ai.requests.paidOf', { paid: a.vcPaid, handled: a.vcHandled }), dim: !a.vcPaid },
        { label: t('ai.requests.vip'), value: fmt(a.vipSent), strong: true },
      ],
    },
  ];

  const top2 = interrupts.slice(0, 2);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: C.divider }}>
          {kpis.map((k) => (
            <div key={k.label} title={k.tip} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '12px 16px', background: C.card, minWidth: 0 }}>
              <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, whiteSpace: 'nowrap' }}>{k.label}</span>
              <span style={{ font: f(700, 18, 1.2), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{k.value}</span>
              <span style={{ font: f(400, 11.5), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.sub}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px 8px', flexWrap: 'wrap', padding: '9px 16px', borderTop: `1px solid ${C.divider}` }}>
          <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, marginRight: 4 }}>{t('ai.path.title')}</span>
          {path.map((p) => (
            <span key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {p.conv && (
                <span title={p.convTip} style={{ display: 'flex', alignItems: 'center', gap: 3, height: 18, padding: '0 6px', borderRadius: 99, background: C.tint16, color: C.purplePale, font: f(600, 11), fontVariantNumeric: 'tabular-nums' }}>
                  <Icon name="arrow-right" size={10} />{p.conv}
                </span>
              )}
              <span style={{ font: f(400, 12.5), color: C.sub, whiteSpace: 'nowrap' }}>{p.label} <span style={{ fontWeight: 600, color: C.text, fontVariantNumeric: 'tabular-nums' }}>{p.value}</span></span>
            </span>
          ))}
          <span style={{ flex: 1 }} />
          <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{t('ai.path.messages', { messages: fmt(a.messages), perChat: fmt(ratio(a.messages, a.chats)) })}</span>
        </div>
      </div>

      <div className="stx-groups" style={{ display: 'grid', gap: 1, background: C.divider, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {groups.map((g) => (
          <div key={g.title} style={{ display: 'flex', flexDirection: 'column', background: C.card, minWidth: 0, paddingBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px' }}>
              <Icon name={g.icon} size={13} color={C.purpleLt} />
              <span style={{ flex: 1, font: f(600, 12.5), color: C.text }}>{g.title}</span>
              <span style={{ font: f(700, 13), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{g.total}</span>
              {g.link && onOpenSubTab && (
                <button type="button" title={g.link.label} onClick={() => onOpenSubTab(g.link!.to)} className="cpt-hover" style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, borderRadius: 5, background: 'transparent', color: C.purpleLt, cursor: 'pointer' }}>
                  <Icon name="arrow-up-right" size={13} />
                </button>
              )}
            </div>
            {g.rows.map((r) => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 28, padding: '0 14px' }}>
                <span style={{ flex: 1, minWidth: 0, font: f(400, 12.5), color: r.dim ? C.muted : C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.label}</span>
                {r.aside && <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.aside}</span>}
                <span style={{ minWidth: 44, textAlign: 'right', font: f(r.strong ? 600 : 500, 12.5), color: r.dim ? C.faint : C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.value}</span>
              </div>
            ))}
            {g.note && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, margin: '6px 14px 2px', font: f(400, 11.5), color: C.purplePale }}>
                <Icon name="alert-circle" size={12} style={{ marginTop: 2 }} />
                <span>
                  {g.note}{' '}
                  {g.link && onOpenSubTab && <button type="button" onClick={() => onOpenSubTab(g.link!.to)} style={{ border: 0, padding: 0, background: 'transparent', color: C.purpleLt, font: f(600, 11.5), cursor: 'pointer' }}>{g.link.label}</button>}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 14px' }}>
          <Icon name="pause-circle" size={13} color={C.purpleLt} />
          <span style={{ font: f(600, 12.5), color: C.text }}>{t('ai.interrupts.title')}</span>
          <span style={{ font: f(400, 11.5), color: C.muted, flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t('ai.interrupts.hint')}</span>
          <span style={{ font: f(700, 13), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{fmt(intTotal)}</span>
          <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap' }}>{t('ai.kpi.ofChats', { value: share(intTotal, a.chats) })}</span>
        </div>
        {interrupts.length ? (
          <>
            <div style={{ columns: '2 340px', columnGap: 28, padding: '0 14px 4px' }}>
              {shownInts.map((x) => (
                <div key={x.name} style={{ breakInside: 'avoid', display: 'grid', gridTemplateColumns: '16px minmax(0,1fr) 40px 44px 30px', gap: 8, alignItems: 'center', height: 28 }}>
                  <Icon name={x.icon} size={12} color={C.muted} />
                  <span title={x.label} style={{ font: f(400, 12.5), color: 'var(--om-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.label}</span>
                  <span style={{ height: 4, borderRadius: 99, background: C.border, overflow: 'hidden' }}><span style={{ display: 'block', height: '100%', width: `${(x.count / intMax) * 100}%`, minWidth: 3, background: C.purple }} /></span>
                  <span style={{ textAlign: 'right', font: f(600, 12.5), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{fmt(x.count)}</span>
                  <span style={{ textAlign: 'right', font: f(400, 11.5), color: C.muted }}>{intTotal && (x.count / intTotal) * 100 < 1 ? '<1' : share(x.count, intTotal)}%</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 14px', borderTop: `1px solid ${C.divider}` }}>
              <Icon name="zap" size={13} color={C.purpleLt} />
              <span style={{ flex: '1 1 280px', font: f(400, 12), color: C.purplePale }}>
                {top2[0] && top2[1] ? t('ai.interrupts.insight', { first: top2[0].label, second: top2[1].label, value: share(top2[0].count + top2[1].count, intTotal) }) : ''}
              </span>
              {interrupts.length > 6 && (
                <button type="button" onClick={() => setAllInts((v) => !v)} className="cpt-hover" style={{ height: 24, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 5, border: 0, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {allInts ? t('ai.interrupts.showTop') : t('ai.interrupts.showAll', { count: interrupts.length })}
                  <Icon name="chevron-down" size={12} style={{ transform: allInts ? 'rotate(180deg)' : 'none' }} />
                </button>
              )}
            </div>
          </>
        ) : (
          <div style={{ padding: '4px 14px 12px', font: f(400, 12.5), color: C.muted }}>{t('ai.interrupts.empty')}</div>
        )}
      </div>
    </div>
  );
}
